import React, { useEffect, useMemo, useState } from "react";
import { Box, Chip, Skeleton, Stack, Typography } from "@mui/material";
import { ChevronDown, ChevronRight } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";
import sh from "bundles/common/utils/sh";
import { SectionLabel } from "bundles/Internal/components/platform_analytics/PlatformAnalyticsShared";

const TAB_LABELS = {
  cv_theque: "CV library",
  applications: "Kanban",
  jobs: "Jobs",
  dashboard: "Dashboard",
  messages: "Messages",
  invoices: "Invoices",
};

const END_REASON_LABELS = {
  closed: "Closed",
  logged_out: "Logged out",
  away_timeout: "Away > 30 min",
  idle_timeout: "Idle > 30 min",
  browser_crashed: "Browser crashed",
  killed_in_background: "Browser crashed (tab killed in background)",
  no_end_received: "Browser crashed or connection lost",
  switched_tab: "Switched tab",
  refined: "Refined",
  cleared: "Cleared filters",
  left_tab: "Left the tab",
};

const formatSeconds = (total) => {
  const seconds = Math.max(0, Math.round(Number(total) || 0));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${String(minutes).padStart(2, "0")}m`;
  if (minutes > 0) return `${minutes}m ${String(seconds % 60).padStart(2, "0")}s`;
  return `${seconds}s`;
};

const formatDay = (value, options = { day: "numeric", month: "short" }) =>
  new Date(`${value}T00:00:00`).toLocaleDateString("en-GB", options);

const formatAxisSeconds = (value) => {
  if (value >= 3600) return `${Math.round((value / 3600) * 10) / 10}h`;
  return `${Math.round(value / 60)}m`;
};

const DayTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const rows = payload.filter((entry) => entry.value > 0).reverse();
  const total = rows.reduce((sum, entry) => sum + entry.value, 0);
  return (
    <Box sx={{ backgroundColor: "hsl(150, 30%, 12%)", borderRadius: "8px", px: 1.5, py: 1, minWidth: 170 }}>
      <Box sx={{ fontSize: 11, color: "hsl(150, 10%, 70%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
        {formatDay(label, { weekday: "long", day: "numeric", month: "long" })}
      </Box>
      <Box sx={{ fontSize: 14, color: "#fff", fontFamily: "Segoe UI Semibold, sans-serif, system-ui", mt: 0.25, mb: rows.length ? 0.75 : 0 }}>
        {total > 0 ? `${formatSeconds(total)} active` : "No activity"}
      </Box>
      {rows.map((entry) => (
        <Box
          key={entry.dataKey}
          sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, fontSize: 12, lineHeight: 1.7, color: "hsl(150, 10%, 85%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: "2px", backgroundColor: entry.color }} />
            {entry.name}
          </Box>
          <span style={{ fontVariantNumeric: "tabular-nums", color: "#fff" }}>{formatSeconds(entry.value)}</span>
        </Box>
      ))}
    </Box>
  );
};

const formatDateTime = (value) => {
  if (!value) return "";
  return new Date(value).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatTime = (value) => {
  if (!value) return "…";
  return new Date(value).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
};

const summarizeFilters = (filters) => {
  let parsed = filters;
  if (typeof filters === "string") {
    try {
      parsed = JSON.parse(filters);
    } catch {
      return [];
    }
  }
  if (!parsed || typeof parsed !== "object") return [];
  return Object.entries(parsed)
    .filter(([, value]) => (Array.isArray(value) ? value.length > 0 : value != null && value !== false && value !== ""))
    .map(([key, value]) => `${key.replace(/_/g, " ")}: ${Array.isArray(value) ? value.join(", ") : value}`);
};

const ReasonChip = ({ period }) => {
  const label = period.open ? "In progress" : END_REASON_LABELS[period.end_reason] || period.end_reason || "Unknown";
  const crashed = period.crashed;
  return (
    <Chip
      label={label}
      size="small"
      sx={{
        height: 19,
        fontSize: 10.5,
        borderRadius: "999px",
        fontFamily: crashed ? "Segoe UI Semibold, sans-serif" : "Segoe UI Regular, sans-serif",
        backgroundColor: crashed ? "hsl(0 60% 93%)" : period.open ? "hsl(45 80% 92%)" : "hsl(150 20% 93%)",
        color: crashed ? "hsl(0 60% 38%)" : period.open ? "hsl(35 70% 30%)" : "hsl(150 30% 24%)",
      }}
    />
  );
};

const TimeSplit = ({ period }) => (
  <Typography component="span" sx={{ fontSize: 12, color: "hsl(150, 10%, 46%)", fontVariantNumeric: "tabular-nums" }}>
    <strong style={{ color: "hsl(150, 30%, 12%)" }}>{formatSeconds(period.active_seconds)}</strong> active
    {period.away_seconds > 0 && ` · ${formatSeconds(period.away_seconds)} away`}
    {period.away_count > 0 && ` (left ${period.away_count}×)`}
  </Typography>
);

const Kpi = ({ label, value, tone }) => (
  <Box
    sx={{
      flex: "1 1 140px",
      p: 1.5,
      borderRadius: "10px",
      border: "1px solid hsl(140, 8%, 88%)",
      backgroundColor: tone === "danger" ? "hsl(0 60% 98%)" : "#fff",
    }}
  >
    <Typography sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", color: "hsl(150, 10%, 46%)" }}>
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: 20,
        fontFamily: "Segoe UI Semibold, sans-serif",
        color: tone === "danger" ? "hsl(0 60% 38%)" : "hsl(150, 30%, 12%)",
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {value}
    </Typography>
  </Box>
);

const SearchStep = ({ search, position }) => {
  const filters = summarizeFilters(search.filters);
  return (
    <Box sx={{ display: "flex", gap: 1, py: 0.75, borderTop: position > 0 ? "1px dashed hsl(140, 8%, 88%)" : "none" }}>
      <Typography sx={{ fontSize: 11, color: "hsl(190 45% 30%)", fontFamily: "Segoe UI Semibold, sans-serif", minWidth: 18, pt: 0.25 }}>
        {position + 1}.
      </Typography>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
          <Typography sx={{ fontSize: 12.5, color: "hsl(150, 30%, 12%)" }}>
            {search.query ? (
              <>&ldquo;<strong>{search.query}</strong>&rdquo;</>
            ) : (
              <span style={{ color: "hsl(150, 10%, 46%)" }}>No text query</span>
            )}
            {search.result_count != null && (
              <span style={{ color: "hsl(150, 10%, 46%)" }}> · {search.result_count} results</span>
            )}
          </Typography>
          <ReasonChip period={search} />
        </Box>
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mt: 0.25 }}>
          <Typography sx={{ fontSize: 11.5, color: "hsl(150, 10%, 46%)", fontVariantNumeric: "tabular-nums" }}>
            {formatTime(search.started_at)} → {formatTime(search.ended_at)}
          </Typography>
          <TimeSplit period={search} />
          {(search.views > 0 || search.reveals > 0 || search.downloads > 0) && (
            <Typography sx={{ fontSize: 11.5, color: "hsl(150 38% 30%)" }}>
              {[
                search.views > 0 && `${search.views} profile${search.views === 1 ? "" : "s"} opened`,
                search.reveals > 0 && `${search.reveals} revealed`,
                search.downloads > 0 && `${search.downloads} downloaded`,
              ]
                .filter(Boolean)
                .join(" · ")}
            </Typography>
          )}
        </Box>
        {filters.length > 0 && (
          <Stack direction="row" sx={{ mt: 0.5, gap: 0.5, flexWrap: "wrap" }}>
            {filters.map((label) => (
              <Chip key={label} label={label} size="small" sx={{ height: 19, fontSize: 10.5, borderRadius: "999px", backgroundColor: "hsl(190 35% 93%)", color: "hsl(190 45% 30%)" }} />
            ))}
          </Stack>
        )}
      </Box>
    </Box>
  );
};

const TabRow = ({ tab }) => (
  <Box sx={{ pl: 3, pr: 2, py: 1, borderTop: "1px solid hsl(140, 8%, 92%)" }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
      <Typography sx={{ fontSize: 13, fontFamily: "Segoe UI Semibold, sans-serif", color: "hsl(150, 30%, 12%)" }}>
        {TAB_LABELS[tab.tab] || tab.tab}
      </Typography>
      <Typography sx={{ fontSize: 11.5, color: "hsl(150, 10%, 46%)", fontVariantNumeric: "tabular-nums" }}>
        {formatTime(tab.started_at)} → {formatTime(tab.ended_at)}
      </Typography>
      <TimeSplit period={tab} />
      <ReasonChip period={tab} />
    </Box>
    {(tab.chains || []).map((chain, chainIndex) => (
      <Box
        key={chain[0]?.id || chainIndex}
        sx={{ mt: 1, ml: 1, px: 1.5, py: 0.75, borderRadius: "8px", backgroundColor: "hsl(190 40% 98%)", border: "1px solid hsl(190 30% 90%)" }}
      >
        <Typography sx={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.05em", color: "hsl(190 45% 30%)", mb: 0.25 }}>
          Search {chainIndex + 1}
          {chain.length > 1 && ` · refined ${chain.length - 1}×`}
          {` · ${formatSeconds(chain.reduce((sum, search) => sum + search.active_seconds, 0))} active`}
        </Typography>
        {chain.map((search, position) => (
          <SearchStep key={search.id} search={search} position={position} />
        ))}
      </Box>
    ))}
  </Box>
);

const dateKey = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

const EngagementPanel = ({ scope, recruiterId, companyId, period = "month", customRange }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [recruiterFilter, setRecruiterFilter] = useState("all");

  const isCompanyScope = scope === "company";
  const targetId = isCompanyScope ? companyId : recruiterId;
  const startDate = customRange?.startDate;
  const endDate = customRange?.endDate;

  useEffect(() => {
    if (!targetId) return undefined;
    if (period === "custom" && (!startDate || !endDate)) return undefined;
    let cancelled = false;
    const params = { period };
    if (period === "custom") {
      params.start_date = startDate;
      params.end_date = endDate;
    }
    if (isCompanyScope && recruiterFilter !== "all") params.recruiter_id = recruiterFilter;
    setRefreshing(true);
    const resource = isCompanyScope ? "companies" : "recruiters";
    sh.get(`/internal/${resource}/${targetId}/engagement`, { params })
      .then((res) => {
        if (!cancelled) setData(res.data);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          setRefreshing(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [targetId, isCompanyScope, period, startDate, endDate, recruiterFilter]);

  const totals = data?.totals || {};
  const byTab = data?.by_tab || [];
  const daily = data?.daily || [];
  const sessions = data?.sessions || [];
  const maxTab = useMemo(() => Math.max(1, ...byTab.map((row) => row.active_seconds)), [byTab]);
  const recruiterOptions = data?.recruiters || [];
  const chartData = useMemo(() => {
    if (daily.length === 0) return [];
    const byDate = Object.fromEntries(daily.map((row) => [row.date, row.by_tab]));
    const today = new Date();
    const rangeStart = data?.range?.start ? new Date(data.range.start) : new Date(`${daily[0].date}T00:00:00`);
    const rangeEnd = data?.range?.end ? new Date(Math.min(new Date(data.range.end), today)) : today;
    const cursor = new Date(rangeStart.getFullYear(), rangeStart.getMonth(), rangeStart.getDate());
    const rows = [];
    while (cursor <= rangeEnd && rows.length < 400) {
      const key = dateKey(cursor);
      rows.push({ date: key, ...(byDate[key] || {}) });
      cursor.setDate(cursor.getDate() + 1);
    }
    return rows;
  }, [daily, data?.range?.start, data?.range?.end]);

  const note = isCompanyScope
    ? "Time spent on the recruiter dashboard by every recruiter in this company. Active time only counts while the tab is visible, focused and used in the last 2 minutes. God-view sessions are never recorded."
    : "Time this recruiter spent on the dashboard. Active time only counts while the tab is visible, focused and used in the last 2 minutes. God-view sessions are never recorded.";

  return (
    <Box>
      <SectionLabel note={note}>{isCompanyScope ? "Time on platform: all recruiters" : "Time on platform"}</SectionLabel>

      <Box sx={{ mt: 1.5 }} />

      {loading ? (
        <Stack sx={{ gap: 1 }}>
          <Skeleton variant="rounded" height={72} />
          <Skeleton variant="rounded" height={160} />
          <Skeleton variant="rounded" height={240} />
        </Stack>
      ) : !data || totals.sessions === 0 ? (
        <Typography sx={{ fontSize: 13, color: "hsl(150, 10%, 46%)", py: 3, textAlign: "center" }}>
          No tracked sessions for {(data?.range?.label || "this period").toLowerCase()}.
        </Typography>
      ) : (
        <Stack sx={{ gap: 2, opacity: refreshing ? 0.5 : 1, transition: "opacity 150ms ease" }}>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Kpi label="Active time" value={formatSeconds(totals.active_seconds)} />
            <Kpi label="Away time" value={formatSeconds(totals.away_seconds)} />
            <Kpi label="Sessions" value={totals.sessions} />
            <Kpi label="Avg. active / session" value={formatSeconds(totals.average_session_seconds)} />
            {totals.crashed_sessions > 0 && <Kpi label="Browser crashed" value={totals.crashed_sessions} tone="danger" />}
          </Box>

          <Box sx={{ p: 2, borderRadius: "10px", border: "1px solid hsl(140, 8%, 88%)", backgroundColor: "#fff" }}>
            <Typography sx={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.05em", color: "hsl(150, 10%, 46%)", mb: 1 }}>
              Time per tab
            </Typography>
            <Stack sx={{ gap: 0.75 }}>
              {byTab.map((row) => (
                <Box key={row.tab} sx={{ display: "grid", gridTemplateColumns: "110px 1fr auto", alignItems: "center", gap: 1.5 }}>
                  <Typography sx={{ fontSize: 12.5, color: "hsl(150, 30%, 12%)" }}>{TAB_LABELS[row.tab] || row.tab}</Typography>
                  <Box sx={{ height: 10, borderRadius: "999px", backgroundColor: "hsl(140, 8%, 94%)", overflow: "hidden" }}>
                    <Box sx={{ width: `${(row.active_seconds / maxTab) * 100}%`, height: "100%", backgroundColor: "hsl(150 38% 30%)" }} />
                  </Box>
                  <Typography sx={{ fontSize: 12, color: "hsl(150, 10%, 46%)", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                    <strong style={{ color: "hsl(150, 30%, 12%)" }}>{formatSeconds(row.active_seconds)}</strong>
                    {row.away_seconds > 0 && ` · ${formatSeconds(row.away_seconds)} away`} · {row.visits} visit{row.visits === 1 ? "" : "s"}
                  </Typography>
                </Box>
              ))}
            </Stack>

            {daily.length > 0 && (
              <>
                <Typography sx={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.05em", color: "hsl(150, 10%, 46%)", mt: 2.5, mb: 1 }}>
                  Active time per day
                </Typography>
                <Box sx={{ height: 200 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={{ top: 4, right: 8, bottom: 0, left: -8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(140, 8%, 90%)" vertical={false} />
                      <XAxis
                        dataKey="date"
                        tickFormatter={(value) => formatDay(value)}
                        tick={{ fontSize: 11, fill: "hsl(150, 10%, 46%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}
                        axisLine={{ stroke: "hsl(140, 8%, 88%)" }}
                        tickLine={false}
                        interval="preserveStartEnd"
                        minTickGap={24}
                      />
                      <YAxis
                        tickFormatter={formatAxisSeconds}
                        tick={{ fontSize: 11, fill: "hsl(150, 10%, 46%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}
                        axisLine={false}
                        tickLine={false}
                        allowDecimals={false}
                        width={40}
                      />
                      <Tooltip content={<DayTooltip />} cursor={{ fill: "hsl(140, 8%, 95%)" }} />
                      <Legend
                        iconType="square"
                        iconSize={8}
                        wrapperStyle={{ fontSize: 11, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150, 10%, 46%)", paddingTop: 4 }}
                      />
                      {[
                        ["cv_theque", "CV library", "hsl(150, 38%, 17%)"],
                        ["applications", "Kanban", "hsl(217, 91%, 55%)"],
                        ["jobs", "Jobs", "hsl(38, 92%, 50%)"],
                        ["dashboard", "Dashboard", "hsl(174, 60%, 45%)"],
                        ["messages", "Messages", "hsl(262, 65%, 55%)"],
                        ["invoices", "Invoices", "hsl(150, 8%, 70%)"],
                      ]
                        .filter(([key]) => byTab.some((row) => row.tab === key && row.active_seconds > 0))
                        .map(([key, name, color]) => (
                          <Bar key={key} dataKey={key} name={name} stackId="tabs" fill={color} maxBarSize={28} />
                        ))}
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </>
            )}
          </Box>

          <Box sx={{ borderRadius: "10px", border: "1px solid hsl(140, 8%, 88%)", backgroundColor: "#fff", overflow: "hidden" }}>
            <Box sx={{ px: 2, pt: 1.5, pb: 1, display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
              <Typography sx={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.05em", color: "hsl(150, 10%, 46%)" }}>
                Sessions{data.truncated && " (latest 100)"}
              </Typography>
              {isCompanyScope && recruiterOptions.length > 1 && (
                <Stack direction="row" sx={{ gap: 0.75, flexWrap: "wrap", alignItems: "center" }}>
                  {[{ id: "all", name: "All recruiters" }, ...recruiterOptions].map((option) => {
                    const selected = String(recruiterFilter) === String(option.id);
                    return (
                      <Chip
                        key={option.id}
                        label={option.sessions ? `${option.name} · ${option.sessions}` : option.name}
                        size="small"
                        onClick={() => setRecruiterFilter(String(option.id))}
                        sx={{
                          height: 24,
                          fontSize: 11.5,
                          borderRadius: "999px",
                          cursor: "pointer",
                          fontFamily: "Segoe UI Semibold, sans-serif",
                          backgroundColor: selected ? "hsl(150 25% 92%)" : "hsl(140 8% 94%)",
                          color: selected ? "hsl(150, 38%, 17%)" : "hsl(150, 10%, 46%)",
                          border: selected ? "1px solid hsl(150, 38%, 17%)" : "1px solid transparent",
                          "&:hover": { backgroundColor: selected ? "hsl(150 25% 92%)" : "hsl(140 8% 90%)" },
                        }}
                      />
                    );
                  })}
                </Stack>
              )}
            </Box>
            {sessions.length === 0 && (
              <Typography sx={{ fontSize: 12.5, color: "hsl(150, 10%, 46%)", px: 2, pb: 1.5 }}>
                No sessions for this recruiter in this period.
              </Typography>
            )}
            {sessions.map((session) => {
              const isOpen = !!expanded[session.id];
              const durationSeconds = session.ended_at
                ? (new Date(session.ended_at) - new Date(session.started_at)) / 1000
                : null;
              return (
                <Box key={session.id} sx={{ borderTop: "1px solid hsl(140, 8%, 88%)" }}>
                  <Box
                    component="button"
                    type="button"
                    onClick={() => setExpanded((prev) => ({ ...prev, [session.id]: !prev[session.id] }))}
                    sx={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flexWrap: "wrap",
                      px: 2,
                      py: 1.25,
                      border: "none",
                      background: session.crashed ? "hsl(0 60% 98%)" : "none",
                      cursor: "pointer",
                      textAlign: "left",
                      "&:hover": { backgroundColor: session.crashed ? "hsl(0 60% 96%)" : "hsl(140 8% 97%)" },
                    }}
                  >
                    {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    <Typography sx={{ fontSize: 12.5, fontFamily: "Segoe UI Semibold, sans-serif", color: "hsl(150, 30%, 12%)", fontVariantNumeric: "tabular-nums" }}>
                      {formatDateTime(session.started_at)}
                    </Typography>
                    {isCompanyScope && (
                      <Chip
                        label={session.recruiter_name || `#${session.recruiter_id}`}
                        size="small"
                        sx={{ height: 18, fontSize: 10, borderRadius: "999px", backgroundColor: "hsl(217 45% 94%)", color: "hsl(217 70% 30%)" }}
                      />
                    )}
                    {durationSeconds != null && (
                      <Typography sx={{ fontSize: 12, color: "hsl(150, 10%, 46%)" }}>{formatSeconds(durationSeconds)} total</Typography>
                    )}
                    <TimeSplit period={session} />
                    <ReasonChip period={session} />
                  </Box>
                  {isOpen && (
                    <Box sx={{ pb: 1 }}>
                      {session.tabs.length === 0 ? (
                        <Typography sx={{ fontSize: 12, color: "hsl(150, 10%, 46%)", pl: 3, py: 1 }}>
                          No dashboard tab recorded for this session.
                        </Typography>
                      ) : (
                        session.tabs.map((tab) => <TabRow key={tab.id} tab={tab} />)
                      )}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Stack>
      )}
    </Box>
  );
};

export default EngagementPanel;
